import { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form, Table } from "react-bootstrap";
import { FaRegSave, FaPlus, FaMinus, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { getFullName } from "../../../../utils/helpers";
import { getProfilePictureUrl } from "../../../../utils/imageUtils";
import { fetchRoles } from "../../../../redux/slices/rolesSlice";
import SearchBar from "../../../reusableComps/SearchBar/SearchBar";

const MEMBERS_PER_PAGE = 10;

const AVATAR_PALETTE = [
    '#F87171', '#FB923C', '#FBBF24', '#34D399',
    '#22D3EE', '#60A5FA', '#818CF8', '#C084FC',
    '#F472B6', '#4ADE80', '#38BDF8', '#FCA5A5'
];

const getAvatarColor = (user) => {
    const name = `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim();
    const code = name ? name.toUpperCase().charCodeAt(0) : 0;
    return AVATAR_PALETTE[code % AVATAR_PALETTE.length];
};

const SectionHeading = ({ children, aside }) => (
    <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
            <div style={{ width: '4px', height: '20px', backgroundColor: 'var(--brand-primary)', borderRadius: '2px' }} />
            <h5 className="m-0 txt-main fw-bold">{children}</h5>
        </div>
        {aside}
    </div>
);

const EditTeamModal = ({ initialName, initialDescription, initialMembers, onSave, onCancel }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    
    const allUsers = useSelector((state) => state.users.items);
    const roles = useSelector((state) => state.roles.items);

    const [name, setName] = useState(initialName || "");
    const [description, setDescription] = useState(initialDescription || "");
    const [membersSelected, setMembersSelected] = useState(initialMembers || []);
    
    const [showMembersModal, setShowMembersModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedPage, setSelectedPage] = useState(1);
    const [availablePage, setAvailablePage] = useState(1);

    useEffect(() => {
        dispatch(fetchRoles());
    }, [dispatch]);

    useEffect(() => {
        setName(initialName || "");
        setDescription(initialDescription || "");
        setMembersSelected(initialMembers || []);
    }, [initialName, initialDescription, initialMembers]);

    const getUserId = (memberObj) => memberObj?.user?.id || memberObj?.user_id || memberObj?.id;

    const hydratedSelectedMembers = useMemo(() => {
        return membersSelected.map(member => {
            const userId = getUserId(member);
            const fullUser = allUsers.find(u => u.id === userId) || member.user || {};
            const roleId = fullUser.role_id || fullUser.role?.id;
            const matchedRole = roles.find(r => r.id === roleId);
            const roleName = fullUser.role?.name || matchedRole?.name || (typeof fullUser.role === 'string' ? fullUser.role : t("components.editTeamModal.table.noRole"));

            const pic = getProfilePictureUrl(fullUser);

            return { originalMember: member, userId, userObj: fullUser, name: getFullName(fullUser) || "N/A", roleName, pic };
        }).filter(m => m.userId);
    }, [membersSelected, allUsers, roles, t]);

    const availableMembers = useMemo(() => {
        const selectedIds = hydratedSelectedMembers.map(m => m.userId);
        return allUsers.filter(u => !selectedIds.includes(u.id)).map(fullUser => {
            const roleId = fullUser.role_id || fullUser.role?.id;
            const matchedRole = roles.find(r => r.id === roleId);
            const roleName = fullUser.role?.name || matchedRole?.name || (typeof fullUser.role === 'string' ? fullUser.role : t("components.editTeamModal.table.noRole"));
            
            const pic = getProfilePictureUrl(fullUser);
            
            return { userId: fullUser.id, userObj: fullUser, name: getFullName(fullUser) || "N/A", roleName, pic };
        });
    }, [allUsers, hydratedSelectedMembers, roles, t]);

    const paginatedSelected = useMemo(() => {
        const start = (selectedPage - 1) * MEMBERS_PER_PAGE;
        return hydratedSelectedMembers.slice(start, start + MEMBERS_PER_PAGE);
    }, [hydratedSelectedMembers, selectedPage]);

    const filteredAvailableMembers = availableMembers.filter(m => m.name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const paginatedAvailable = useMemo(() => {
        const start = (availablePage - 1) * MEMBERS_PER_PAGE;
        return filteredAvailableMembers.slice(start, start + MEMBERS_PER_PAGE);
    }, [filteredAvailableMembers, availablePage]);

    const totalSelectedPages = Math.max(1, Math.ceil(hydratedSelectedMembers.length / MEMBERS_PER_PAGE));
    const totalAvailablePages = Math.max(1, Math.ceil(filteredAvailableMembers.length / MEMBERS_PER_PAGE));

    const handleAddMember = (userToAdd) => {
        setMembersSelected(prev => [...prev, { user_id: userToAdd.userId, user: userToAdd.userObj }]);
    };

    const handleRemoveMember = (userIdToRemove) => {
        setMembersSelected(prev => prev.filter(m => getUserId(m) !== userIdToRemove));
    };

    return (
        <>
            <Modal show onHide={onCancel} centered size="lg" contentClassName="modal-content">
                <Modal.Header closeButton className="border-bottom-0 pb-0">
                    <Modal.Title className="fw-bold txt-main">{t("components.editTeamModal.title")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Control type="text" value={name} onChange={(e) => setName(e.target.value)} className="fw-bold" />
                    </Form.Group>
                    <Form.Group className="mb-4">
                        <Form.Control as="textarea" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
                    </Form.Group>
                    <SectionHeading aside={
                        <Button size="sm" onClick={() => { setAvailablePage(1); setShowMembersModal(true); }}>
                            <FaPlus size={12} /> {t("components.editTeamModal.buttons.addMembers")}
                        </Button>
                    }>{t("components.editTeamModal.sections.members")}</SectionHeading>
                    <div className="atlar-card p-3">
                        <Table borderless>
                            <tbody>
                                {paginatedSelected.map((member) => (
                                    <tr key={member.userId}>
                                        <td style={{ width: 50 }}>
                                            {member.pic ? <img src={member.pic} className="rounded-circle" style={{ width: 35, height: 35 }} /> : <div className="d-flex align-items-center justify-content-center rounded-circle" style={{ width: 35, height: 35, backgroundColor: getAvatarColor(member.userObj), color: 'white' }}>{member.name.charAt(0)}</div>}
                                        </td>
                                        <td>{member.name}</td>
                                        <td>{member.roleName}</td>
                                        <td className="text-end"><Button variant="link" onClick={() => handleRemoveMember(member.userId)}><FaMinus size={12} /></Button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        {totalSelectedPages > 1 && (
                            <div className="d-flex justify-content-between mt-2">
                                <Button disabled={selectedPage === 1} onClick={() => setSelectedPage(p => p - 1)}><FaChevronLeft /></Button>
                                <span>{selectedPage} / {totalSelectedPages}</span>
                                <Button disabled={selectedPage >= totalSelectedPages} onClick={() => setSelectedPage(p => p + 1)}><FaChevronRight /></Button>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="link" onClick={onCancel}>{t("components.editTeamModal.buttons.cancel")}</Button>
                    <Button onClick={() => onSave({ name, description, members: membersSelected })}><FaRegSave /> {t("components.editTeamModal.buttons.save")}</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showMembersModal} onHide={() => setShowMembersModal(false)} centered>
                <Modal.Body>
                    <SearchBar value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setAvailablePage(1); }} />
                    <Table className="mt-3">
                        <tbody>
                            {paginatedAvailable.map((m) => (
                                <tr key={m.userId}>
                                    <td>{m.name}</td>
                                    <td className="text-end"><Button onClick={() => handleAddMember(m)}><FaPlus size={12} /></Button></td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <div className="d-flex justify-content-between mt-2">
                        <Button disabled={availablePage === 1} onClick={() => setAvailablePage(p => p - 1)}><FaChevronLeft /></Button>
                        <span>{availablePage} / {totalAvailablePages}</span>
                        <Button disabled={availablePage >= totalAvailablePages} onClick={() => setAvailablePage(p => p + 1)}><FaChevronRight /></Button>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default EditTeamModal;